import React, { useEffect, useState } from 'react'
import { useIntl, FormattedNumber } from 'react-intl'
import { useConfig } from 'msp-integrations'
import { getAssetUrl } from 'pwa-kit-react-sdk/ssr/universal/utils'
import { getCustomerCreditUsed } from '@utils/basketUtils'
import { Availability, Headline } from '../../../index'
import * as T from './OrderSummary.types'
import * as S from './OrderSummary.styles'

export const OrderSummaryLineItem = ({
  getProductAvailability,
  product,
  currency
}: T.OrderSummaryLineItemProps) => {
  const { formatMessage } = useIntl()

  const [inventory, setInventory] = useState(product.c_inventory)
  const fallBackImage = getAssetUrl('static/img/fallback-product-image.png')

  const handleImageNotFound = (el: HTMLImageElement) => {
    if (el.src !== fallBackImage) {
      el.src = fallBackImage
    }
  }

  useEffect(() => {
    if (!inventory) {
      getProductAvailability(product.productId).then(({ inventory }: any) => {
        setInventory(inventory)
      })
    }
  }, [])

  let priceAfterOrderDiscount = product.priceAfterOrderDiscount

  if (product.priceAdjustments?.length) {
    priceAfterOrderDiscount += getCustomerCreditUsed(product.priceAdjustments)
  }

  return (
    <div className={S.OrderSumaryLineItemWrapper}>
      <div className={S.OrderSumaryLineItemContent}>
        <div className={S.OrderSumaryLineItemImage}>
          <img
            src={product.c_image}
            alt={product.productName}
            onError={(ev) => handleImageNotFound(ev.target as HTMLImageElement)}
          />
        </div>
        <div className={S.OrderSumaryLineItemDetails}>
          <div className={S.OrderSumaryLineItemTitle}>
            {product.productName}
          </div>
        </div>
      </div>
      <div className={S.OrderSummaryLineItemBasketDetails}>
        <div className={`${S.CheckoutContentText} mb-1 self-end`}>
          <span>
            {formatMessage({
              defaultMessage: 'Quantity:',
              id: 'msp.checkout-quantity'
            })}
          </span>
          <span className={S.OrderSumaryLineItemQuantity}>
            {product.quantity}
          </span>
        </div>
        <div className={S.OrderSummaryProductPriceWrapper}>
          {priceAfterOrderDiscount < product.price ? (
            <div className={S.CheckoutBasePriceText}>
              <FormattedNumber
                value={product.price}
                style='currency'
                currency={currency}
                currencyDisplay='code'
              />
            </div>
          ) : null}
          <div className={S.CheckoutContentText}>
            <FormattedNumber
              value={priceAfterOrderDiscount || product.price}
              style='currency'
              currency={currency}
              currencyDisplay='code'
            />
          </div>
        </div>
      </div>
      {inventory && (
        <div className={S.OrderSumaryAvailability}>
          <Availability {...inventory} />
        </div>
      )}
    </div>
  )
}

export const OrderSummaryCategoryList = ({
  categoryList,
  giftCertificateItems = [],
  c_groupedByCategories,
  getProductAvailability,
  currency
}: T.OrderSummaryCategoryListProps) => {
  const { formatMessage } = useIntl()

  return (
    <>
      {categoryList &&
        categoryList.map((category: string) => {
          const catItem = c_groupedByCategories[category]
          return (
            <div key={catItem.categoryName} className={S.CategoryItemWrapper}>
              <Headline tag='h6' className='mb-4'>
                {catItem.categoryName}
              </Headline>
              {catItem.products.map((product, index) => (
                <OrderSummaryLineItem
                  key={`order-sum-${index}-${product.id}`}
                  getProductAvailability={getProductAvailability}
                  product={product}
                  currency={currency}
                />
              ))}
            </div>
          )
        })}

      {giftCertificateItems.length > 0 && (
        <div className={S.CategoryItemWrapper}>
          <Headline tag='h6' className='mb-4'>
            {formatMessage({
              defaultMessage: 'Gift Cards',
              id: 'msp.list.groups.giftcards.title'
            })}
          </Headline>
          {giftCertificateItems.map((giftCertificateItem: any, index) => {
            let product = {
              itemId: giftCertificateItem.giftCertificateItemId,
              productId: giftCertificateItem.c_originalSKU,
              productName: giftCertificateItem.c_productName,
              c_image: giftCertificateItem.c_image,
              c_inventory: giftCertificateItem.c_inventory,
              c_purchasable: giftCertificateItem.c_purchasable,
              inventory: giftCertificateItem.c_inventory,
              quantity: 1,
              basePrice: giftCertificateItem.amount,
              price: giftCertificateItem.amount,
              priceAfterItemDiscount: giftCertificateItem.amount,
              priceAfterOrderDiscount: giftCertificateItem.amount,
              itemText: ''
            }

            return (
              <OrderSummaryLineItem
                key={`order-sum-${index}-${product.itemId}`}
                getProductAvailability={getProductAvailability}
                product={product}
                currency={currency}
              />
            )
          })}
        </div>
      )}
    </>
  )
}

export const OrderSummaryTotals = ({
  showShipment,
  showTotal = true,
  orderTotal,
  productTotal,
  productSubTotal,
  currency,
  shippingItems,
  customerCreditUsed = 0,
  giftCertificateTotal = 0
}: T.OrderSummaryTotalsProps) => {
  const { formatMessage } = useIntl()
  const {
    theme: { font }
  } = useConfig()
  const hasShippingMethod = shippingItems && shippingItems[0]
  const shippingDiscount = hasShippingMethod
    ? shippingItems[0].priceAfterItemDiscount - shippingItems[0].price
    : 0

  return (
    <>
      {showShipment && productTotal && (
        <div className={`${font.regular} ${S.CheckoutContentLabel}`}>
          <span>
            {formatMessage({
              defaultMessage: 'Total amount of items',
              id: 'msp.checkout.products.subtotal'
            })}
          </span>
          <span className={`${font.regular} ${S.CheckoutContentPrice}`}>
            <FormattedNumber
              value={productTotal}
              style='currency'
              currency={currency}
              currencyDisplay='code'
            />
          </span>
        </div>
      )}
      {showShipment && hasShippingMethod && (
        <div className={`${font.regular} ${S.CheckoutContentLabel}`}>
          <span>
            {formatMessage({
              defaultMessage: 'Shipping',
              id: 'order_summary.label.shipping'
            })}
          </span>
          <span className={`${font.regular} ${S.CheckoutContentPrice}`}>
            <FormattedNumber
              value={shippingItems[0].price}
              style='currency'
              currency={currency}
              currencyDisplay='code'
            />
          </span>
        </div>
      )}
      {showShipment && (
        <div className='mb-2'>
          {customerCreditUsed ? (
            <div className={`mb-2 ${S.CheckoutContentDiscountLabel}`}>
              <span>
                {formatMessage({
                  defaultMessage: 'Item Credit',
                  id: 'msp.checkout.promotions.applied.items'
                })}
              </span>
              <span className={S.CheckoutContentDiscountLabel}>
                <FormattedNumber
                  value={-Math.abs(customerCreditUsed)}
                  style='currency'
                  currency={currency}
                  currencyDisplay='code'
                />
              </span>
            </div>
          ) : null}
          {hasShippingMethod && Boolean(shippingDiscount) && (
            <div className={`${S.CheckoutContentDiscountLabel}`}>
              <span>
                {formatMessage({
                  defaultMessage: 'Shipping Credit',
                  id: 'msp.checkout.promotions.applied.shipping'
                })}
              </span>
              <span className={S.CheckoutContentDiscountLabel}>
                <FormattedNumber
                  value={shippingDiscount}
                  style='currency'
                  currency={currency}
                  currencyDisplay='code'
                />
              </span>
            </div>
          )}
        </div>
      )}
      {showTotal && (
        <div className={S.CheckoutContentLabelWithBorder}>
          <span className={S.CheckoutFlexCenter}>
            <span>
              {formatMessage({
                defaultMessage: 'Total amount',
                id: 'msp.checkout.totalamount'
              })}
            </span>
            <span className={S.CheckoutTaxes}>
              {showShipment
                ? formatMessage({
                    defaultMessage: 'incl. VAT',
                    id: 'msp.cart.total.amount.vat'
                  })
                : formatMessage({
                    defaultMessage: 'incl. VAT, excl. shipping',
                    id: 'msp.cart.total.amount.only.vat'
                  })}
            </span>
          </span>
          <span className={S.CheckoutContentPrice}>
            <FormattedNumber
              value={showShipment ? orderTotal : (productTotal - customerCreditUsed)}
              style='currency'
              currency={currency}
              currencyDisplay='code'
            />
          </span>
        </div>
      )}
    </>
  )
}
export default OrderSummaryTotals
